import classNames from 'classnames';
import { type ChangeEvent, type FC, type PropsWithChildren, useEffect, useRef } from 'react';

import VisualBox from '../shared/VisualBox';

import s from './CheckBox.module.scss';

type Props = {
  className?: string;
  id?: string;
  isChecked: boolean;
  isIndeterminate?: boolean;
  hasError?: boolean;
  label: string;
  onChange: (newIsChecked: boolean, event?: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  size?: 'sm' | 'md';
  style?: Parameters<typeof VisualBox>[0]['style'];
  disabled?: boolean;
};

const CheckBox: FC<PropsWithChildren<Props>> = ({
  className,
  id,
  isChecked,
  isIndeterminate,
  hasError,
  label,
  onChange,
  onFocus,
  size = 'md',
  style = 'square',
  children,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = isIndeterminate || false;
    }
  }, [isIndeterminate]);

  return (
    <label
      className={classNames(
        s.container,
        size === 'md' && s.md,
        size === 'sm' && s.sm,
        !!hasError && s.error,
        className,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type='checkbox'
        id={id}
        checked={isChecked}
        aria-checked={isIndeterminate ? 'mixed' : isChecked}
        aria-invalid={hasError || undefined}
        onChange={(event) => {
          onChange(event.target.checked, event);
        }}
        onFocus={onFocus}
        disabled={disabled}
      />
      <VisualBox isChecked={isChecked} isIndeterminate={isIndeterminate} size={size} style={style} />
      {label}
      {children}
    </label>
  );
};

export default CheckBox;
